import { useEffect, type JSX } from 'react';
import { isRouteErrorResponse, useLocation, useRouteError } from 'react-router';

import { ButtonLink } from '@/components/ui/Button';
import { reportError } from '@/lib/errorReporting';

/**
 * Route-level error boundary. It renders inside the shell, so the header,
 * navigation and footer stay usable when a single route fails.
 */
export function RouteError(): JSX.Element {
  const error = useRouteError();
  const { pathname } = useLocation();

  useEffect(() => {
    reportError(error, { route: pathname });
  }, [error, pathname]);

  const isNotFound = isRouteErrorResponse(error) && error.status === 404;

  return (
    <div className="shell-container py-24 lg:py-32">
      <div className="max-w-xl">
        <p className="font-mono text-xs uppercase tracking-[0.18em] text-accent">
          {isNotFound ? '404' : 'Error'}
        </p>
        <h1 className="mt-3 text-3xl sm:text-4xl">
          {isNotFound ? 'Page not found' : 'This page failed to load'}
        </h1>
        <p className="mt-4 text-muted">
          {isNotFound
            ? 'The page you were looking for does not exist. The links below cover everything on this site.'
            : 'Something went wrong while rendering this page. The rest of the site is still available.'}
        </p>
        <div className="mt-8 flex flex-wrap gap-3">
          <ButtonLink to="/">Go to the home page</ButtonLink>
          <ButtonLink to="/case-studies" variant="secondary">
            Browse case studies
          </ButtonLink>
        </div>
      </div>
    </div>
  );
}
